import createClient, { type FetchResponse, type Middleware, type ClientOptions } from 'openapi-fetch';
import type { HttpMethod } from 'openapi-typescript-helpers';
import type { FetchConfig, OptionalClientMethod, RequiredClientMethod } from '../api/helper-types';
import type { NloError } from '#imports';

type XSRFTokenFunction = () => Promise<string>;

type NloFetcherConfigOptions = {
	/**
	 * The base url to the vertical .NET api.
	 */
	baseUrl: string;

	/**
	 * Function that parses the error into a NloError type, use this to parse errors into the type returned from your vertical
	 * @param input error thrown during api call.
	 * @returns the parsed error.
	 */
	errorParser: (input: any) => NloError;

	/**
	 * The api call that provides xsrf tokens.
	 */
	tokenApiCall?: XSRFTokenFunction;

	/**
	 * Additional middleware to add.
	 */
	verticalMiddleWare?: Middleware[];

	/**
	 * Additional headers to add to all fetch requests.
	 */
	customHeaders?: Record<string, string>;

	/**
	 * The pageId of the page making the api call.
	 */
	pageId?: string;
};

/**
 * Returns the fetcher config for calling api endpoints of our .NET api's.
 * @param options the options {@link NloFetcherConfigOptions} for the fetcher config.
 */
export function getNloFetcherConfig(options: NloFetcherConfigOptions): FetchConfig {
	const middleware: Middleware[] = [];

	if (options.tokenApiCall) {
		middleware.push(getTokenMiddleware(options.tokenApiCall));
	}

	middleware.push(getErrorParsingMiddleware(options.errorParser));

	if (options.verticalMiddleWare) {
		middleware.push(...options.verticalMiddleWare);
	}

	const headers = {
		// Add header to let the .NET api know this request comes from the nuxt client.
		'X-Requested-With': 'XMLHttpRequest',
		...options.customHeaders,
	} as Record<string, string>;

	if (options.pageId) {
		headers['Cms-Page-Id'] = options.pageId;
	}

	const clientOptions: ClientOptions = {
		baseUrl: options.baseUrl,
		credentials: 'include',
		headers,
	};

	const fetcherConfig: FetchConfig = {
		createClient: <Paths extends Record<string, Record<HttpMethod, object>>>() => {
			const client = createClient<Paths, 'application/json'>(clientOptions);
			for (let i = 0; i < middleware.length; i++) {
				client.use(middleware[i]);
			}

			const ensureGetData: RequiredClientMethod<Paths, 'get'> = (path, ...init) =>
				handleResponseEnsureData(client.GET(path, ...init), options.errorParser);

			const postData: OptionalClientMethod<Paths, 'post'> = (path, ...init) =>
				handleResponse(client.POST(path, ...init), options.errorParser);

			const putData: OptionalClientMethod<Paths, 'put'> = (path, ...init) => handleResponse(client.PUT(path, ...init), options.errorParser);

			const deleteData: OptionalClientMethod<Paths, 'delete'> = (path, ...init) =>
				handleResponse(client.DELETE(path, ...init), options.errorParser);

			const patchData: OptionalClientMethod<Paths, 'patch'> = (path, ...init) =>
				handleResponse(client.PATCH(path, ...init), options.errorParser);

			return {
				...client,
				GET: ensureGetData,
				POST: postData,
				PUT: putData,
				DELETE: deleteData,
				PATCH: patchData,
				ensureData,
			};
		},
	};

	return fetcherConfig;
}

/**
 * Fetcher middleware that parses errors into known types.
 * @param errorParser the function that parses the error into a NloError type.
 */
const getErrorParsingMiddleware = (errorParser: (input: any) => NloError): Middleware => {
	return {
		onError: ({ error }) => {
			throw errorParser(error);
		},
	};
};

const tokenSupportedRequestMethods = ['POST', 'PATCH', 'DELETE', 'PUT'];

/**
 * Fetcher middleware that ensures we apply XSRF tokens on POST requests.
 * @param tokenApiCall a function that returns a promise with the token from the .NET server.
 */
const getTokenMiddleware = (tokenApiCall: XSRFTokenFunction): Middleware => {
	return {
		onRequest: async (options) => {
			if (options.request.method && tokenSupportedRequestMethods.includes(options.request.method)) {
				const token = await tokenApiCall();
				options.request.headers.append('RequestVerificationToken', token);
			}
			return options.request;
		},
	};
};

/**
 * Utility function that ensures data from an openapi-fetch API request is present.
 * @param request the request coming from the openapi-fetch client (e.g. `client.GET('/api/my/common/account/profile')`)
 * @param errorParser the function that parses the error into a NloError type.
 * @returns an object containing the response data.
 */
async function handleResponseEnsureData<ResponseType extends Record<string | number, any>, Options>(
	request: Promise<FetchResponse<ResponseType, Options, 'application/json'>>,
	errorParser: (input: any) => NloError,
): Promise<NonNullable<FetchResponse<ResponseType, Options, 'application/json'>['data']>> {
	const data = await handleResponse(request, errorParser);

	if (data) {
		return data;
	}

	// Server didn't respond.
	throw new NloUnknownError({ message: 'No data returned from server' });
}

/**
 * Utility function that parses the response from an openapi-fetch API request and throws an error if the response is not successful.
 * @param request the request coming from the openapi-fetch client (e.g. `client.GET('/api/my/common/account/profile')`)
 * @param errorParser the function that parses the error into a NloError type.
 * @returns an object containing the response data.
 */
async function handleResponse<ResponseType extends Record<string | number, any>, Options>(
	request: Promise<FetchResponse<ResponseType, Options, 'application/json'>>,
	errorParser: (input: any) => NloError,
): Promise<FetchResponse<ResponseType, Options, 'application/json'>['data']> {
	const { data, error, response } = await request;

	if (response.ok) {
		return data;
	}

	if (response.status) {
		// Server responded, but not successful.
		throw errorParser(new NloApiError(response.status, error));
	}

	// Server didn't respond.
	throw errorParser(new NloUnknownError({ message: error }));
}

/**
 * Ensures that the provided promise resolves to a non-undefined value.
 * If the promise resolves to undefined, an error is thrown.
 *
 * This function is useful for handling API responses where data is expected.
 * It can be used for GET requests as well as POST, PATCH, DELETE, or PUT requests
 * where the response is expected to contain data.
 * @template T - The type of the resolved value.
 * @param Promise<T | undefined> request - The promise to be resolved.
 * @returns Promise<T> - A promise that resolves to a non-undefined value.
 * @throws NloUnknownError - If the promise resolves to undefined.
 */
const ensureData: <T>(request: Promise<T | undefined>) => Promise<T> = async (request) => {
	const response = await request;
	if (response) {
		return response;
	}

	throw new NloUnknownError({ message: 'No data returned from server' });
};
